import { Action, Location } from 'history';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Admin2FAModal,
    Alert,
    autoinject,
    bind,
    ErrorStore,
    ErrorView,
    ImpersonatingAlert,
    inject,
    LoadGoogleMapScript,
    MainContainer,
    ModalHandle,
    NotificationAlert,
    NotificationToastList,
    PrivateRoute,
    SidebarObfuscator,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsView } from '../Accounts/AccountsView';
import { AdminView } from '../Admin/AdminView';
import { AuditStore } from '../Audit/AuditStore';
import { BIView } from '../BI/BIView';
import { CloudConfigsView } from '../CloudConfigs/CloudConfigsView';
import { CsvView } from '../Csv/CsvView';
import { WarrantyAlertsView } from '../WarrantyAlerts/WarrantyAlertsView';
import { DisplayEventProfilesView } from '../DisplayEventProfiles/DisplayEventProfilesView';
import { DisplayGroupsView } from '../DisplayGroups/DisplayGroupsView';
import { DisplayList } from '../Displays/DisplayList';
import { DisplayView } from '../Displays/DisplayView/DisplayView';
import { FavoritesList } from '../Displays/FavoritesList';
import { ModelDefinitionsView } from '../ModelDefinitons/ModelDefinitionsView';
import { NocServersView } from '../NocServers/NocServersView';
import { PartDefinitionsView } from '../PartDefinitions/PartDefinitionsView';
import { PartsView } from '../Parts/PartsView';
import { ProductionProjectsView } from '../ProductionProjects/ProductionProjectsView';
import { SoftwareUpdatesView } from '../SoftwareUpdates/SoftwareUpdatesView';
import { TicketsView } from '../Tickets/TicketsView';
import { WarehouseView } from '../Warehouses/WarehouseView';
import { AppStore } from './AppStore';
import { Header } from './Header';
import { ProductionHome } from './ProductionHome';
import { Sidebar } from './Sidebar';
import { UpdateModal } from './UpdateModal';
import { ServiceKitView } from '../ServiceKits/ServiceKitView';
import { CustomerServiceDashboardView } from '../CustomerServiceDashboard/views/CustomerServiceDashboardView';
import { DisplayGrid } from '../Displays/DisplayGrid/DisplayGrid';
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";
import { DisplayGridView } from '../Displays/DisplayGrid/DisplayGridView';
import {IdleCheck} from "./IdleCheck";

const googleMapApiKey = (window as any).initialGoogleMapApiKey as string;

const agGridLicenseKey = (window as any).initialAgGridLicenseKey as string;

LicenseManager.setLicenseKey(agGridLicenseKey);

export interface IAppProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class App extends React.Component<IAppProps> {
    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.accountStore.loadUserAsync();
            const user = this.accountStore.user;

            if (user) {
                this.appStore.storage.theme = user.preferredTheme as any;
                
                this.accountStore.mergeOldAccountStorageValues();
                
                if (this.appStore.storage.version != user.viewedChangelog) {
                    // Uncomment when we fix the changelog
                    // this.updateModalHandle.open();

                    try {
                        user.viewedChangelog = this.appStore.storage.version;
                        await this.accountStore.setViewedChangelog(this.appStore.storage.version);
                    } catch (e: any) {
                        console.error(e);
                    }
                }

                if (this.accountStore.storage.displaysPreference != 'grid' && this.accountStore.storage.displaysPreference != 'list') {
                    this.accountStore.storage.displaysPreference = 'grid';
                }
            }
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }

        await this.reportPageView(this.props.location);
        this.props.history.listen(this.onHistoryChange);
    }

    public render() {
        if (this.loading) return <div>Loading...</div>;

        const authority = (window as any).initialOidcConfig ? (window as any).initialOidcConfig.authority : '';

        const isInternalUser = this.accountStore.isInRoles(['MriAdmin', 'MriUser']);
        const isCustomerSupportUser = this.accountStore.isInRoles(['MriCustomerServiceAdmin', 'MriCustomerServiceRep']);

        return (
            <ErrorView error={this.appStore.error} errorStore={this.errorStore}>
                <div className={this.layoutClassName}>
                    <IdleCheck/>
                    
                    <Header history={this.props.history} onMenuLinkClick={this.toggleSidebar} />

                    <NotificationToastList />

                    <Sidebar
                        closeSidebar={this.closeSidebar}
                        collapseSidebar={this.collapseSidebar}
                        sidebarCollapsed={this.appStore.storage.sidebarCollapsed && !this.appStore.isMedium}
                        sidebarVisible={this.sidebarVisible}
                        match={this.props.match}
                        history={this.props.history}
                        location={this.props.location}
                        staticContext={this.props.staticContext}
                    />

                    <SidebarObfuscator onClick={this.closeSidebar} active={this.sidebarVisible} />

                    <MainContainer mainCointainerRef={this.setMainContainer} onScroll={this.updateScrollPosition}>
                        <span className="text-center">
                            <Alert alertHandle={this.appStore.alertHandle} />
                        </span>
                        <span>
                            <NotificationAlert notificationHandle={this.appStore.notificationHandle} />
                        </span>

                        <ImpersonatingAlert
                            isImpersonating={this.accountStore.user?.isImpersonating ?? false}
                            onClick={this.accountStore.signoutRedirect}
                        />
                       
                        {!isInternalUser && (
                            <PrivateRoute
                                path="/"
                                exact
                                user={this.accountStore.user}
                                roles={['ProdTestUser']}
                                component={ProductionHome}
                            />
                        )}

                        {isInternalUser && this.accountStore.storage.displaysPreference != 'grid' &&
                            <>
                                <PrivateRoute
                                    path="/"
                                    exact
                                    user={this.accountStore.user}
                                    roles={['MriAdmin', 'MriUser']}
                                    component={DisplayList}
                                />
                                <PrivateRoute
                                    path="/displays"
                                    exact
                                    user={this.accountStore.user}
                                    roles={['MriAdmin', 'MriUser']}
                                    component={DisplayList}
                                />
                            </>
                        }

                        {isInternalUser && this.accountStore.storage.displaysPreference == 'grid' &&
                            <>
                                <PrivateRoute
                                    path="/"
                                    exact
                                    user={this.accountStore.user}
                                    roles={['MriAdmin', 'MriUser']}
                                    component={DisplayGridView}
                                />
                                <PrivateRoute
                                    path="/displays"
                                    exact
                                    user={this.accountStore.user}
                                    roles={['MriAdmin', 'MriUser']}
                                    component={DisplayGridView}
                                />
                            </>
                        }

                        <PrivateRoute
                            path="/bi"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={BIView}
                        />
                        <PrivateRoute
                            path="/favorites"
                            exact
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={FavoritesList}
                        />
                        <PrivateRoute
                            path="/displays/:serialNumber"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={DisplayView}
                        />
                        <PrivateRoute
                            path="/display-event-profiles"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={DisplayEventProfilesView}
                        />
                        <PrivateRoute
                            path="/noc-servers"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={NocServersView}
                        />
                        <PrivateRoute
                            path="/customer-service-dashboard"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser', 'MriCustomerServiceAdmin', 'MriCustomerServiceRep']}
                            component={CustomerServiceDashboardView}
                        />
                        <PrivateRoute
                            path="/tickets"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={TicketsView}
                        />
                        <PrivateRoute
                            path="/accounts"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={AccountsView}
                        />
                        <PrivateRoute
                            path="/display-groups"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={DisplayGroupsView}
                        />
                        <PrivateRoute
                            path="/warehouses"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={WarehouseView}
                        />
                        <PrivateRoute
                            path="/production-projects"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdUser']}
                            component={ProductionProjectsView}
                        />
                        <PrivateRoute
                            path="/model-definitions"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdUser']}
                            component={ModelDefinitionsView}
                        />
                        <PrivateRoute
                            path="/software-updates"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdTestUser']}
                            component={SoftwareUpdatesView}
                        />
                        <PrivateRoute
                            path="/cloud-configs"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdTestUser']}
                            component={CloudConfigsView}
                        />
                        <PrivateRoute
                            path="/csv"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'MriUser']}
                            component={CsvView}
                        />
                        <PrivateRoute
                            path="/admin"
                            user={this.accountStore.user}
                            roles={['MriAdmin']}
                            component={AdminView}
                        />
                        <PrivateRoute
                            path="/part-definitions"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdTestUser']}
                            component={PartDefinitionsView}
                        />
                        <PrivateRoute
                            path="/service-kits"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdTestUser']}
                            component={ServiceKitView}
                        />
                        <PrivateRoute
                            path="/parts"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdTestUser']}
                            component={PartsView}
                        />
                        <PrivateRoute
                            path="/warranty-alerts"
                            user={this.accountStore.user}
                            roles={['MriAdmin', 'ProdUser']}
                            component={WarrantyAlertsView}
                        />
                    </MainContainer>

                    <Admin2FAModal
                        location={this.props.location}
                        url={`${authority}/preferences`}
                        requires2FA={
                            !!this.accountStore.user &&
                            !this.accountStore.user.twoFactorEnabled &&
                            !this.accountStore.isTestUser &&
                            this.accountStore.isInRole('MriAdmin')
                        }
                    />
                    <UpdateModal modalHandle={this.updateModalHandle} />
                    <LoadGoogleMapScript googleMapApiKey={googleMapApiKey} />
                </div>
            </ErrorView>
        );
    }

    @bind
    @action
    private updateScrollPosition() {
        if (this.appStore.mainContainer) {
            this.appStore.storage.lastMainContainerScrollPositionX = this.appStore.mainContainer.scrollLeft;
            this.appStore.storage.lastMainContainerScrollPositionY = this.appStore.mainContainer.scrollTop;
        }
    }

    @bind
    @action
    private setMainContainer(r: HTMLDivElement | null) {
        this.appStore.mainContainer = r;
    }

    @bind
    @action
    private async toggleSidebar(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.sidebarVisible = !this.sidebarVisible;
    }

    @bind
    @action
    private async closeSidebar(event: React.MouseEvent<LinkContainer | HTMLAnchorElement | HTMLDivElement>) {
        this.sidebarVisible = false;
    }

    @bind
    private onHistoryChange(location: Location, trigger: Action): any {
        this.appStore.showHeader();

        this.reportPageView(location);
    }

    private async reportPageView(location: Location) {
        try {
            const match = /^\/displays\/(?<displaySerialNumber>[A-Z0-9]+)/.exec(location.pathname);
            const displaySerialNumber = match && match.groups ? match.groups.displaySerialNumber : null;

            await this.auditStore.logPageView(location.pathname, displaySerialNumber);
        } catch (e: any) {
            console.error(e, 'Failed to logPageView');
        }
    }

    @bind
    @action
    private collapseSidebar() {
        this.appStore.storage.sidebarCollapsed = !this.appStore.storage.sidebarCollapsed;
    }

    @computed
    private get layoutClassName() {
        return `layout-container ${
            !this.appStore.storage.sidebarCollapsed ? 'layout-sidebar' : 'layout-sidebar-collapsed'
        }`;
    }

    @observable
    private sidebarVisible = false;
    @observable
    private loading = true;
    private updateModalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private auditStore!: AuditStore;
    @inject
    private errorStore!: ErrorStore;
}
